/**
 * FluxNodeAlertSettings Component
 *
 * UI for configuring FluxNode monitoring alerts.
 * Allows users to customize which alerts they receive and how.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import { useFluxNodeAlertSettings } from '../../hooks/useFluxNodeAlertSettings';

interface ToggleProps {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}

function Toggle({ checked, onChange, disabled }: ToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={onChange}
      style={{
        width: '44px',
        height: '24px',
        borderRadius: '12px',
        border: 'none',
        padding: '2px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        backgroundColor: checked ? 'var(--primary)' : 'var(--border)',
        opacity: disabled ? 0.5 : 1,
        transition: 'background-color 0.2s ease',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <span
        style={{
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          backgroundColor: 'white',
          transition: 'transform 0.2s ease',
          transform: checked ? 'translateX(20px)' : 'translateX(0)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
        }}
      />
    </button>
  );
}

interface SettingRowProps {
  icon: React.ReactNode;
  label: string;
  description?: string;
  children: React.ReactNode;
}

function SettingRow({ icon, label, description, children }: SettingRowProps) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 0',
      borderBottom: '1px solid var(--border)',
      gap: '12px',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flex: 1 }}>
        <span style={{ color: 'var(--muted)', marginTop: '2px' }}>{icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 500, fontSize: '14px', color: 'var(--foreground)' }}>{label}</div>
          {description && (
            <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>{description}</div>
          )}
        </div>
      </div>
      <div style={{ flexShrink: 0 }}>{children}</div>
    </div>
  );
}

interface FluxNodeAlertSettingsProps {
  onClose?: () => void;
}

export function FluxNodeAlertSettings({ onClose }: FluxNodeAlertSettingsProps) {
  const { t } = useTranslation();
  const {
    settings,
    isLoading,
    isSaving,
    isAuthenticated,
    saveSettings,
    toggleSetting,
    updateNumericSetting,
    resetToDefaults,
  } = useFluxNodeAlertSettings();

  const [expandedSection, setExpandedSection] = useState<'nodes' | 'apps' | 'delivery' | null>('nodes');

  if (!isAuthenticated) {
    return (
      <div className="card" style={{ padding: '24px', textAlign: 'center' }}>
        <Icons.Lock size={48} style={{ color: 'var(--muted)', marginBottom: '16px' }} />
        <h3 style={{ marginBottom: '8px' }}>{t('fluxNode.alerts.loginRequired', 'Connexion requise')}</h3>
        <p style={{ color: 'var(--muted)', fontSize: '14px' }}>
          {t('fluxNode.alerts.loginDescription', 'Connectez-vous pour configurer les alertes de monitoring FluxNode.')}
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="card" style={{ padding: '24px', textAlign: 'center' }}>
        <Icons.Loader size={32} style={{ animation: 'spin 1s linear infinite', marginBottom: '12px' }} />
        <p style={{ color: 'var(--muted)' }}>{t('common.loading', 'Chargement...')}</p>
      </div>
    );
  }

  const toggleSection = (section: 'nodes' | 'apps' | 'delivery') => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        borderBottom: '1px solid var(--border)',
        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(99, 102, 241, 0.1))',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Icons.Bell size={24} style={{ color: 'var(--primary)' }} />
          <div>
            <h3 style={{ margin: 0, fontSize: '16px' }}>
              {t('fluxNode.alerts.title', 'Alertes de Monitoring')}
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'var(--muted)' }}>
              {t('fluxNode.alerts.subtitle', 'Recevez des notifications par email')}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {isSaving && (
            <span style={{ fontSize: '12px', color: 'var(--primary)' }}>
              <Icons.Loader size={14} style={{ animation: 'spin 1s linear infinite', marginRight: '4px' }} />
              {t('common.saving', 'Enregistrement...')}
            </span>
          )}
          {onClose && (
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--muted)',
                cursor: 'pointer',
                padding: '4px',
              }}
            >
              <Icons.X size={20} />
            </button>
          )}
        </div>
      </div>

      {/* Master toggle */}
      <div style={{
        padding: '16px 20px',
        background: settings.email_enabled ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
        borderBottom: '1px solid var(--border)',
      }}>
        <SettingRow
          icon={<Icons.Mail size={20} />}
          label={t('fluxNode.alerts.emailEnabled', 'Alertes par email')}
          description={t('fluxNode.alerts.emailEnabledDesc', 'Recevoir des notifications par email en cas de changement')}
        >
          <Toggle
            checked={settings.email_enabled}
            onChange={() => toggleSetting('email_enabled')}
          />
        </SettingRow>
      </div>

      {settings.email_enabled && (
        <>
          {/* Node Alerts Section */}
          <div>
            <button
              onClick={() => toggleSection('nodes')}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 20px',
                background: expandedSection === 'nodes' ? 'rgba(139, 92, 246, 0.05)' : 'transparent',
                border: 'none',
                borderBottom: '1px solid var(--border)',
                cursor: 'pointer',
                color: 'var(--foreground)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Icons.Server size={18} style={{ color: 'var(--primary)' }} />
                <span style={{ fontWeight: 600, fontSize: '14px' }}>
                  {t('fluxNode.alerts.nodeAlerts', 'Alertes Nodes')}
                </span>
              </div>
              <Icons.ChevronDown
                size={18}
                style={{
                  transform: expandedSection === 'nodes' ? 'rotate(180deg)' : 'rotate(0)',
                  transition: 'transform 0.2s ease',
                  color: 'var(--muted)',
                }}
              />
            </button>

            {expandedSection === 'nodes' && (
              <div style={{ padding: '0 20px' }}>
                <SettingRow
                  icon={<Icons.AlertTriangle size={18} />}
                  label={t('fluxNode.alerts.nodeOffline', 'Node hors ligne')}
                  description={t('fluxNode.alerts.nodeOfflineDesc', 'Alerte quand un node disparait du reseau')}
                >
                  <Toggle
                    checked={settings.alert_node_offline}
                    onChange={() => toggleSetting('alert_node_offline')}
                  />
                </SettingRow>

                <SettingRow
                  icon={<Icons.Plus size={18} />}
                  label={t('fluxNode.alerts.nodeNew', 'Nouveau node')}
                  description={t('fluxNode.alerts.nodeNewDesc', 'Alerte quand un nouveau node apparait')}
                >
                  <Toggle
                    checked={settings.alert_node_new}
                    onChange={() => toggleSetting('alert_node_new')}
                  />
                </SettingRow>

                <SettingRow
                  icon={<Icons.TrendingUp size={18} />}
                  label={t('fluxNode.alerts.rankChange', 'Changement de rang')}
                  description={t('fluxNode.alerts.rankChangeDesc', 'Alerte sur changement significatif de rang')}
                >
                  <Toggle
                    checked={settings.alert_rank_change}
                    onChange={() => toggleSetting('alert_rank_change')}
                  />
                </SettingRow>

                {settings.alert_rank_change && (
                  <SettingRow
                    icon={<Icons.Hash size={18} />}
                    label={t('fluxNode.alerts.rankThreshold', 'Seuil de rang')}
                    description={t('fluxNode.alerts.rankThresholdDesc', 'Alerter si le rang change de plus de X positions')}
                  >
                    <select
                      aria-label={t('fluxNode.alerts.rankThreshold', 'Seuil de rang')}
                      value={settings.alert_rank_threshold}
                      onChange={(e) => updateNumericSetting('alert_rank_threshold', parseInt(e.target.value))}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: '1px solid var(--border)',
                        background: 'var(--card)',
                        color: 'var(--foreground)',
                        fontSize: '13px',
                        cursor: 'pointer',
                      }}
                    >
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={200}>200</option>
                      <option value={500}>500</option>
                    </select>
                  </SettingRow>
                )}

                <SettingRow
                  icon={<Icons.Layers size={18} />}
                  label={t('fluxNode.alerts.tierChange', 'Changement de tier')}
                  description={t('fluxNode.alerts.tierChangeDesc', 'Alerte sur upgrade/downgrade de tier')}
                >
                  <Toggle
                    checked={settings.alert_tier_change}
                    onChange={() => toggleSetting('alert_tier_change')}
                  />
                </SettingRow>

                <SettingRow
                  icon={<Icons.DollarSign size={18} />}
                  label={t('fluxNode.alerts.paymentReceived', 'Paiement recu')}
                  description={t('fluxNode.alerts.paymentReceivedDesc', 'Alerte a chaque paiement (peut etre frequent)')}
                >
                  <Toggle
                    checked={settings.alert_payment_received}
                    onChange={() => toggleSetting('alert_payment_received')}
                  />
                </SettingRow>
              </div>
            )}
          </div>

          {/* App Alerts Section */}
          <div>
            <button
              onClick={() => toggleSection('apps')}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 20px',
                background: expandedSection === 'apps' ? 'rgba(139, 92, 246, 0.05)' : 'transparent',
                border: 'none',
                borderBottom: '1px solid var(--border)',
                cursor: 'pointer',
                color: 'var(--foreground)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Icons.Grid size={18} style={{ color: 'var(--primary)' }} />
                <span style={{ fontWeight: 600, fontSize: '14px' }}>
                  {t('fluxNode.alerts.appAlerts', 'Alertes Applications')}
                </span>
              </div>
              <Icons.ChevronDown
                size={18}
                style={{
                  transform: expandedSection === 'apps' ? 'rotate(180deg)' : 'rotate(0)',
                  transition: 'transform 0.2s ease',
                  color: 'var(--muted)',
                }}
              />
            </button>

            {expandedSection === 'apps' && (
              <div style={{ padding: '0 20px' }}>
                <SettingRow
                  icon={<Icons.AlertCircle size={18} />}
                  label={t('fluxNode.alerts.appOffline', 'Application hors ligne')}
                  description={t('fluxNode.alerts.appOfflineDesc', 'Alerte quand une app est retiree du reseau')}
                >
                  <Toggle
                    checked={settings.alert_app_offline}
                    onChange={() => toggleSetting('alert_app_offline')}
                  />
                </SettingRow>

                <SettingRow
                  icon={<Icons.Plus size={18} />}
                  label={t('fluxNode.alerts.appNew', 'Nouvelle application')}
                  description={t('fluxNode.alerts.appNewDesc', 'Alerte quand une nouvelle app est deployee')}
                >
                  <Toggle
                    checked={settings.alert_app_new}
                    onChange={() => toggleSetting('alert_app_new')}
                  />
                </SettingRow>

                <SettingRow
                  icon={<Icons.Clock size={18} />}
                  label={t('fluxNode.alerts.appExpiring', 'Application expirant')}
                  description={t('fluxNode.alerts.appExpiringDesc', 'Alerte avant expiration d\'une app')}
                >
                  <Toggle
                    checked={settings.alert_app_expiring}
                    onChange={() => toggleSetting('alert_app_expiring')}
                  />
                </SettingRow>

                {settings.alert_app_expiring && (
                  <SettingRow
                    icon={<Icons.Calendar size={18} />}
                    label={t('fluxNode.alerts.expireDays', 'Jours avant expiration')}
                    description={t('fluxNode.alerts.expireDaysDesc', 'Alerter X jours avant l\'expiration')}
                  >
                    <select
                      aria-label={t('fluxNode.alerts.expireDays', 'Jours avant expiration')}
                      value={settings.alert_app_expire_days}
                      onChange={(e) => updateNumericSetting('alert_app_expire_days', parseInt(e.target.value))}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: '1px solid var(--border)',
                        background: 'var(--card)',
                        color: 'var(--foreground)',
                        fontSize: '13px',
                        cursor: 'pointer',
                      }}
                    >
                      <option value={3}>3 {t('common.days', 'jours')}</option>
                      <option value={7}>7 {t('common.days', 'jours')}</option>
                      <option value={14}>14 {t('common.days', 'jours')}</option>
                      <option value={30}>30 {t('common.days', 'jours')}</option>
                    </select>
                  </SettingRow>
                )}
              </div>
            )}
          </div>

          {/* Delivery Settings Section */}
          <div>
            <button
              onClick={() => toggleSection('delivery')}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 20px',
                background: expandedSection === 'delivery' ? 'rgba(139, 92, 246, 0.05)' : 'transparent',
                border: 'none',
                borderBottom: '1px solid var(--border)',
                cursor: 'pointer',
                color: 'var(--foreground)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Icons.Settings size={18} style={{ color: 'var(--primary)' }} />
                <span style={{ fontWeight: 600, fontSize: '14px' }}>
                  {t('fluxNode.alerts.deliverySettings', 'Parametres de livraison')}
                </span>
              </div>
              <Icons.ChevronDown
                size={18}
                style={{
                  transform: expandedSection === 'delivery' ? 'rotate(180deg)' : 'rotate(0)',
                  transition: 'transform 0.2s ease',
                  color: 'var(--muted)',
                }}
              />
            </button>

            {expandedSection === 'delivery' && (
              <div style={{ padding: '0 20px' }}>
                <SettingRow
                  icon={<Icons.Moon size={18} />}
                  label={t('fluxNode.alerts.quietHours', 'Heures de silence')}
                  description={t('fluxNode.alerts.quietHoursDesc', 'Pas d\'alertes pendant ces heures (UTC)')}
                >
                  <Toggle
                    checked={settings.quiet_hours_enabled}
                    onChange={() => toggleSetting('quiet_hours_enabled')}
                  />
                </SettingRow>

                {settings.quiet_hours_enabled && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 0',
                    paddingLeft: '32px',
                    borderBottom: '1px solid var(--border)',
                  }}>
                    <span style={{ fontSize: '13px', color: 'var(--muted)' }}>
                      {t('common.from', 'De')}:
                    </span>
                    <input
                      type="time"
                      value={settings.quiet_hours_start}
                      onChange={(e) => saveSettings({ quiet_hours_start: e.target.value })}
                      style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: '1px solid var(--border)',
                        background: 'var(--card)',
                        color: 'var(--foreground)',
                        fontSize: '13px',
                      }}
                    />
                    <span style={{ fontSize: '13px', color: 'var(--muted)' }}>
                      {t('common.to', 'A')}:
                    </span>
                    <input
                      type="time"
                      value={settings.quiet_hours_end}
                      onChange={(e) => saveSettings({ quiet_hours_end: e.target.value })}
                      style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: '1px solid var(--border)',
                        background: 'var(--card)',
                        color: 'var(--foreground)',
                        fontSize: '13px',
                      }}
                    />
                    <span style={{ fontSize: '11px', color: 'var(--muted)' }}>(UTC)</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Footer */}
      <div style={{
        padding: '16px 20px',
        borderTop: '1px solid var(--border)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: 'var(--card)',
      }}>
        <button
          onClick={resetToDefaults}
          disabled={isSaving}
          style={{
            padding: '8px 16px',
            borderRadius: '6px',
            border: '1px solid var(--border)',
            background: 'transparent',
            color: 'var(--muted)',
            fontSize: '13px',
            cursor: isSaving ? 'not-allowed' : 'pointer',
            opacity: isSaving ? 0.5 : 1,
          }}
        >
          <Icons.RotateCcw size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
          {t('common.resetToDefault', 'Reinitialiser')}
        </button>

        <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
          {t('fluxNode.alerts.autoSave', 'Les modifications sont enregistrees automatiquement')}
        </div>
      </div>
    </div>
  );
}
